import React, { useState } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Button,
    Dialog,
    DialogContent,
    DialogActions,
    Radio,
    Alert,
    TextField,
    InputLabel,
    InputAdornment
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import { gql, useQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import LLMpage from './LLMpage';

// --- GraphQL Definitions ---

// NOTE: This query is no longer used in the main component flow but is kept for context.
const GET_LLM_CONFIG = gql`
  query GetLlmConfig($tenantId: ID!) {
    getLlmObservabilityConfig(tenantId: $tenantId) {
      tenantId
      configurationType
    }
  }
`;

const GET_LLM_DATA_SOURCES = gql`
  query LlmDataSourcesByTenant($tenantId: ID!) {
    llmDataSourcesByTenant(tenantId: $tenantId) {
      id
    }
  }
`;

const CONFIGURE_LLM_OBS = gql`
  mutation ConfigureLlmObs($input: ConfigureLlmObservabilityInput!) {
    configureLlmObservability(input: $input) {
      tenantId
      configurationType
    }
  }
`;


// --- Child & Helper Components ---

const NoTenantEmptyState: React.FC = () => (
    <Box sx={{ background: '#F9F9F9', width: '100%', minHeight: 'calc(100vh - 100px)', display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
        <Box sx={{ backgroundColor: '#FFFFFF', borderRadius: '16px', p: { xs: 4, md: 6 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', width: '100%', maxWidth: '2000px', height: '690px', marginTop: "10px" }}>
            <img src="/icons/no-cost-analysis.svg" alt="No Tenant Selected" style={{ width: '143px', height: '143px', marginBottom: '10px' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontFamily: 'Inter' }}>No Tenant Added</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1, maxWidth: '400px', fontFamily: 'Inter' }}>It looks like you haven't set up a tenant yet. Please add a tenant to begin observibility.</Typography>
        </Box>
    </Box>
);

const DataFetchErrorState: React.FC = () => (
    <Box sx={{ background: '#F9F9F9', width: '100%', minHeight: 'calc(100vh - 100px)', display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
        <Box sx={{ backgroundColor: '#FFFFFF', borderRadius: '16px', p: { xs: 4, md: 6 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', width: '100%', maxWidth: '2000px', height: '690px', marginTop: "10px" }}>
            <img src="/icons/unable_to_diplay_data.svg" alt="Unable to display data" style={{ width: '143px', height: '143px', marginBottom: '10px' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontFamily: 'Inter' }}>Unable to Display Data</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1, maxWidth: '400px', fontFamily: 'Inter', fontSize: '12px' }}>We’re currently experiencing a temporary issue displaying the data. Please try refreshing the page or check back shortly.</Typography>
        </Box>
    </Box>
);

interface NoDataSourcesStateProps {
  onAddDataSource: () => void;
}
const NoDataSourcesState: React.FC<NoDataSourcesStateProps> = ({ onAddDataSource }) => {
  return (
    
    <Box
      sx={{
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        textAlign: 'center',
        backgroundColor: '#FFFFFF', 
        borderRadius: '16px',
        padding:'175px'
      }}
    >
        
      <img src="/icons/no-cost-analysis.svg" alt="Nothing to display" style={{ width: '150px', height: '150px', marginBottom: '24px' }} />
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontFamily:'Inter' }}>
        Nothing to display yet
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: '450px', fontFamily:'Inter', lineHeight: 1.6 }}>
        To view LLM analytics, add a data source using the button below. You can also add it from the Data Source page.
      </Typography>
      <Button variant="contained" onClick={onAddDataSource}
sx={{ backgroundColor:'#0064FF', width:'179px',height:'36px', textTransform: 'none', fontSize:'14px', fontWeight: 500, borderRadius: '8px',color:' #FFFFFF' }}      >
        Add LLM Data Source
      </Button>
    </Box>
  );
};

// NOTE: This modal component is no longer used by the LLMObservability page but is kept for context.
interface LangfuseSelectionModalProps {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  onSuccess: (selection: 'MANAGED' | 'SELF_HOSTED', payload?: any) => void;
}
const LangfuseSelectionModal: React.FC<LangfuseSelectionModalProps> = ({ open, onClose, tenantId, onSuccess }) => {
    const [selection, setSelection] = useState<'MANAGED' | 'SELF_HOSTED' | ''>('');
    const [configure, { loading, error }] = useMutation(CONFIGURE_LLM_OBS);
    const navigate = useNavigate();

    const [formState, setFormState] = useState({
        langfuseUrl: '',
        langfuseEmail: '',
        langfusePassword: '',
        clickhouseUrl: '',
        clickhouseUsername: '',
        clickhousePassword: ''
    });

    const [formErrors, setFormErrors] = useState({
        langfuseUrl: '',
        langfuseEmail: '',
        clickhouseUrl: ''
    });

    const validateField = (name: string, value: string): string => {
        switch (name) {
            case 'langfuseEmail':
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    return "Please enter a valid email address.";
                }
                break;
            case 'langfuseUrl':
            case 'clickhouseUrl':
                if (!value.startsWith('http://') && !value.startsWith('https://')) {
                    return "URL must start with http:// or https://";
                }
                if (/[^a-zA-Z0-9:/.-]/.test(value)) {
                    return "URL contains invalid characters.";
                }
                break;
            default:
                break;
        }
        return '';
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormState(prevState => ({ ...prevState, [name]: value }));
        const errorMessage = validateField(name, value);
        setFormErrors(prevErrors => ({ ...prevErrors, [name]: errorMessage }));
    };

    const isSelfHostedFormFilled = 
        !!formState.langfuseUrl &&
        !!formState.langfuseEmail &&
        !!formState.langfusePassword &&
        !!formState.clickhouseUrl &&
        !!formState.clickhouseUsername &&
        !!formState.clickhousePassword;
    
    const hasValidationErrors = Object.values(formErrors).some(msg => msg !== '');

    const getIsButtonDisabled = () => {
        if (loading) {
            return true;
        }
        if (selection === 'MANAGED') {
            return false;
        }
        if (selection === 'SELF_HOSTED') {
            return !isSelfHostedFormFilled || hasValidationErrors;
        }
        return true;
    };


    const externalLabelStyle = { fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 400, color: '#333333', marginBottom: '6px', display: 'block' };
    const sxTextField = {
      '& .MuiOutlinedInput-root': {
        '& fieldset': { borderRadius: '8px' },
        '&.Mui-focused fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
      },
      '& .MuiInputBase-input': {
        fontFamily: 'Inter, sans-serif', color: '#333333', fontSize: '12px',
        '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
          WebkitBoxShadow: '0 0 0 30px white inset !important', WebkitTextFillColor: `#333333 !important`,
          transition: 'background-color 5000s ease-in-out 0s',
        },
      },
      '& .MoiInputBase-input::placeholder': {
        fontFamily: 'Inter, sans-serif', color: 'rgba(185, 185, 185, 1)', fontSize: '12px', opacity: 1,
      },
    };

    const handleSubmit = async () => {
        if (getIsButtonDisabled()) return;
        
        const input = { tenantId, configurationType: selection, ...formState };
        
        try {
            await configure({ variables: { input } });
            const payload = selection === 'SELF_HOSTED' ? formState : undefined;
            onSuccess(selection as 'MANAGED' | 'SELF_HOSTED', payload);
            navigate('/data-sources/add', { state: { defaultProvider: 'llm', selfHostedPayload: payload } });
        } catch (e) {
            console.error("Failed to save configuration:", e);
        }
    };
    
    const RadioOption = ({ value, title, description }: { value: 'MANAGED' | 'SELF_HOSTED', title: string, description: string }) => {
        const isSelected = selection === value;
        return (
            <Box
                onClick={() => setSelection(value)}
                sx={{
                    width: '100%',
                    minHeight: 85,
                    padding: '10px',
                    borderStyle: 'solid',
                    borderWidth: '1px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                    borderColor: isSelected ? '#0064FF' : '#ECECEC',
                    backgroundColor: isSelected ? '#F3F8FE' : '#FFFFFF',
                }}
            >
                <Radio 
                    checked={isSelected} 
                    value={value}
                    size="small"
                    sx={{ 
                        p: 0, 
                        mr: '6px', 
                        mt: '1px',
                        '&.Moi-checked': {
                          color: '#0064FF',
                        },
                    }} 
                />
                <Box>
                    <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '14px', lineHeight: '21px', color: '#666666' }}>
                        {title}
                    </Typography>
                    <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '12px', lineHeight: '18px', color: '#666666', mt: 0.5 }}>
                        {description}
                    </Typography>
                </Box>
            </Box>
        );
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            PaperProps={{ 
                sx: { 
                    width: 600, 
                    maxWidth: '95%',
                    borderRadius: '16px',
                    p: '8px',
                    fontFamily: 'Inter',
                    opacity: 1,
                    position: 'relative',
                    overflow: 'hidden',
                    backgroundImage: "url('/textures/Texture.svg'), url('/images/Gradient_Popup.svg')",
                    backgroundRepeat: ' no-repeat',
                    backgroundPosition: 'top left, top right',
                } 
            }}
        >
            <DialogContent sx={{ p: '16px', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box sx={{ 
                      mr: 1.5,
                      width: 56, 
                      height: 56,
                      bgcolor: '#E5EFFF', 
                      borderRadius: '10px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                  }}>
                      <img src="/icons/langfuse_selection.svg" alt="Langfuse Icon" style={{ width: '26px', height: '19px' }}/>
                  </Box>
                    <Box>
                        <Typography sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '20px', lineHeight: '115%', color: '#333333' }}>
                            Langfuse Selection
                        </Typography>
                        <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '14px', lineHeight: '150%', color: '#666666' }}>
                            To proceed, select the Langfuse option for this tenant.
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ p: '16px', borderRadius: '12px', my: 3, display: 'flex', alignItems: 'flex-start', gap: '12px', bgcolor: '#F6F4E5', border: '1px solid #A59500' }}>
                    <img src="/icons/watch_icon.svg" alt="Note Icon" style={{ width: '18px', height: '21px' }}/>
                    <Box>
                      <Typography sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '14px', lineHeight: '20px', color: '#333333' }}>
                          Please note
                      </Typography>
                      <Typography component="ul" sx={{ pl: 2.5, m: 0, mt: 1, fontFamily: 'Inter', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#666666', '& li': { mb: 0.5 } }}>
                          <li>This is a one time selection, once confirmed it cannot be changed.</li>
                          <li>All the future LLM Data Source creation for this tenant will use the selected option.</li>
                      </Typography>
                    </Box>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <RadioOption value="MANAGED" title="Use our langfuse solution" description="Lorem ipsum dolor sum it. Lorem ipsum dolor sum it." />
                    <RadioOption value="SELF_HOSTED" title="Use self hosted langfuse solution" description="Lorem ipsum dolor sum it. Lorem ipsum dolor sum it."/>
                </Box>
                {selection === 'SELF_HOSTED' && (
                    <Box sx={{ mt: 3 }}>
                        <Typography sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '16px', mb: 2 }}>
                            Please provide us with your self hosted Langfuse details
                        </Typography>
                        {/* Row 1: Langfuse URL | Clickhouse URL */}
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1.5 }}>
                            <Box sx={{ flex: 1, flexDirection: 'column' }}>
                                <InputLabel htmlFor="langfuseUrl" sx={externalLabelStyle}>Langfuse URL *</InputLabel>
                                <TextField required fullWidth id="langfuseUrl" name="langfuseUrl" size="small" placeholder="Enter Langfuse URL" sx={sxTextField} value={formState.langfuseUrl} onChange={handleInputChange} 
                                    error={!!formErrors.langfuseUrl}
                                    helperText={formErrors.langfuseUrl}
                                    InputProps={{ endAdornment: formState.langfuseUrl && !formErrors.langfuseUrl && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} />
                            </Box>
                            <Box sx={{ flex: 1, flexDirection: 'column' }}>
                                <InputLabel htmlFor="clickhouseUrl" sx={externalLabelStyle}>Clickhouse URL *</InputLabel>
                                <TextField required fullWidth id="clickhouseUrl" name="clickhouseUrl" size="small" placeholder="Enter Clickhouse URL" sx={sxTextField} value={formState.clickhouseUrl} onChange={handleInputChange} 
                                    error={!!formErrors.clickhouseUrl}
                                    helperText={formErrors.clickhouseUrl}
                                    InputProps={{ endAdornment: formState.clickhouseUrl && !formErrors.clickhouseUrl && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} />
                            </Box>
                        </Box>
                        {/* Row 2: Langfuse Email | Clickhouse DB Username */}
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1.5 }}>
                            <Box sx={{ flex: 1, flexDirection: 'column' }}>
                                <InputLabel htmlFor="langfuseEmail" sx={externalLabelStyle}>Langfuse Email *</InputLabel>
                                <TextField required fullWidth id="langfuseEmail" name="langfuseEmail" size="small" placeholder="Enter Langfuse Email" sx={sxTextField} value={formState.langfuseEmail} onChange={handleInputChange} 
                                    error={!!formErrors.langfuseEmail}
                                    helperText={formErrors.langfuseEmail}
                                    InputProps={{ endAdornment: formState.langfuseEmail && !formErrors.langfuseEmail && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} />
                            </Box>
                            <Box sx={{ flex: 1, flexDirection: 'column' }}>
                                <InputLabel htmlFor="clickhouseUsername" sx={externalLabelStyle}>Clickhouse DB Username *</InputLabel>
                                <TextField required fullWidth id="clickhouseUsername" name="clickhouseUsername" size="small" placeholder="Enter Clickhouse DB Username" sx={sxTextField} value={formState.clickhouseUsername} onChange={handleInputChange} InputProps={{ endAdornment: formState.clickhouseUsername && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} />
                            </Box>
                        </Box>
                        {/* Row 3: Langfuse Password | Clickhouse DB Password */}
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1.5 }}>
                            <Box sx={{ flex: 1, flexDirection: 'column' }}>
                                <InputLabel htmlFor="langfusePassword" sx={externalLabelStyle}>Langfuse Password *</InputLabel>
                                <TextField required fullWidth id="langfusePassword" name="langfusePassword" type="password" size="small" placeholder="Enter Langfuse Password" sx={sxTextField} value={formState.langfusePassword} onChange={handleInputChange} InputProps={{ endAdornment: formState.langfusePassword && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} />
                            </Box>
                            <Box sx={{ flex: 1, flexDirection: 'column' }}>
                                <InputLabel htmlFor="clickhousePassword" sx={externalLabelStyle}>Clickhouse DB Password *</InputLabel>
                                <TextField required fullWidth id="clickhousePassword" name="clickhousePassword" type="password" size="small" placeholder="Enter Clickhouse DB Password" sx={sxTextField} value={formState.clickhousePassword} onChange={handleInputChange} InputProps={{ endAdornment: formState.clickhousePassword && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} />
                            </Box>
                        </Box>
                    </Box>
                )}
                
                {error && <Alert severity="error" sx={{ mt: 2 }}>{error.message}</Alert>}
            </DialogContent>

            <DialogActions sx={{ p: '24px', pt: 1, mt: 'auto' }}>
                <Button 
                    onClick={onClose} 
                    sx={{ 
                        width: '79px',
                        height: '36px',
                        backgroundColor: '#F2F7FF',
                        color: '#0064FF',
                        borderRadius: '8px',
                        py: '10px',
                        px: '16px',
                        border: 'none',
                        textTransform: 'none',
                        fontFamily: 'Inter',
                        fontWeight: 500,
                        fontSize: '14px',
                        lineHeight: '16px',
                        '&:hover': {
                            backgroundColor: '#E5F1FF'
                        }
                    }}
                >
                    Cancel
                </Button>
                <Button 
                    variant="contained" 
                    onClick={handleSubmit} 
                    disabled={getIsButtonDisabled()}
                    sx={{
                        boxShadow: 'none',
                        width: '180px',
                        height: '36px',
                        borderRadius: '8px',
                        py: '10px',
                        px: '16px',
                        gap: '4px',
                        textTransform: 'none', 
                        backgroundColor: '#0064FF',
                        color: '#FFFFFF',
                        fontFamily: 'Inter',
                        fontWeight: 500,
                        fontSize: '14px',
                        lineHeight: '16px',
                        '&:hover': {
                            backgroundColor: '#0053D6',
                            boxShadow: 'none',
                        },
                        '&.Moi-disabled': {
                            backgroundColor: '#E5E7EB',
                            color: '#9CA3AF'
                        }
                    }}
                >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Confirm and Proceed'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};


// --- Main Page Component ---

interface LLMObservabilityProps {
  tenantId?: string;
}

const LLMObservability: React.FC<LLMObservabilityProps> = ({ tenantId }) => {
    const navigate = useNavigate();

    // REMOVED the GET_LLM_CONFIG query as it's no longer needed for this flow.
    
    const { data: dataSourcesData, loading: dataSourcesLoading, error: dataSourcesError } = useQuery(GET_LLM_DATA_SOURCES, {
        variables: { tenantId },
        skip: !tenantId,
        fetchPolicy: 'network-only', 
    });

    // MODIFIED: This function now directly navigates without checking for configuration.
    const handleAddDataSourceClick = () => {
        navigate('/data-sources/add', { state: { defaultProvider: 'llm' } });
    };

    // REMOVED the handleConfigurationSuccess function and modal state.

    if (!tenantId) {
        return <NoTenantEmptyState />;
    }

    // MODIFIED: Simplified loading state.
    if (dataSourcesLoading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>;
    }

    // MODIFIED: Simplified error state.
    if (dataSourcesError) {
        console.error("Error fetching data:", dataSourcesError);
        return <DataFetchErrorState />;
    }

    const hasDataSources = dataSourcesData?.llmDataSourcesByTenant?.length > 0;

    if (hasDataSources) {
        return <LLMpage tenantId={tenantId} />;
    }

    return (
        <Box sx={{ p: 3, background: '#F9F9F9', minHeight: 'calc(100vh - 100px)', boxSizing: 'border-box' }}>
            <NoDataSourcesState onAddDataSource={handleAddDataSourceClick} />
            
            {/* REMOVED the LangfuseSelectionModal from being rendered here. */}
        </Box>
    );
};

export default LLMObservability;




// import React, { useState } from 'react';
// import {
//     Box,
//     Typography,
//     CircularProgress,
//     Button,
//     Dialog,
//     DialogContent,
//     DialogActions,
//     Radio,
//     Alert,
//     TextField,
//     InputLabel,
//     InputAdornment
// } from '@mui/material';
// import { Check as CheckIcon } from '@mui/icons-material';
// import { gql, useQuery, useMutation } from '@apollo/client';
// import { useNavigate } from 'react-router-dom';
// import LLMpage from './LLMpage';

// // --- GraphQL Definitions ---

// const GET_LLM_CONFIG = gql`
//   query GetLlmConfig($tenantId: ID!) {
//     getLlmObservabilityConfig(tenantId: $tenantId) {
//       tenantId
//       configurationType
//     }
//   }
// `;

// const GET_LLM_DATA_SOURCES = gql`
//   query LlmDataSourcesByTenant($tenantId: ID!) {
//     llmDataSourcesByTenant(tenantId: $tenantId) {
//       id
//     }
//   }
// `;

// const CONFIGURE_LLM_OBS = gql`
//   mutation ConfigureLlmObs($input: ConfigureLlmObservabilityInput!) {
//     configureLlmObservability(input: $input) {
//       tenantId
//       configurationType
//     }
//   }
// `;


// // --- Child & Helper Components ---

// const NoTenantEmptyState: React.FC = () => (
//     <Box sx={{ background: '#F9F9F9', width: '100%', minHeight: 'calc(100vh - 100px)', display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
//         <Box sx={{ backgroundColor: '#FFFFFF', borderRadius: '16px', p: { xs: 4, md: 6 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', width: '100%', maxWidth: '2000px', height: '690px', marginTop: "10px" }}>
//             <img src="/icons/no-cost-analysis.svg" alt="No Tenant Selected" style={{ width: '143px', height: '143px', marginBottom: '10px' }} />
//             <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontFamily: 'Inter' }}>No Tenant Added</Typography>
//             <Typography variant="body1" color="text.secondary" sx={{ mb: 1, maxWidth: '400px', fontFamily: 'Inter' }}>It looks like you haven't set up a tenant yet. Please add a tenant to begin observibility.</Typography>
//         </Box>
//     </Box>
// );

// const DataFetchErrorState: React.FC = () => (
//     <Box sx={{ background: '#F9F9F9', width: '100%', minHeight: 'calc(100vh - 100px)', display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
//         <Box sx={{ backgroundColor: '#FFFFFF', borderRadius: '16px', p: { xs: 4, md: 6 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', width: '100%', maxWidth: '2000px', height: '690px', marginTop: "10px" }}>
//             <img src="/icons/unable_to_diplay_data.svg" alt="Unable to display data" style={{ width: '143px', height: '143px', marginBottom: '10px' }} />
//             <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontFamily: 'Inter' }}>Unable to Display Data</Typography>
//             <Typography variant="body1" color="text.secondary" sx={{ mb: 1, maxWidth: '400px', fontFamily: 'Inter', fontSize: '12px' }}>We’re currently experiencing a temporary issue displaying the data. Please try refreshing the page or check back shortly.</Typography>
//         </Box>
//     </Box>
// );

// interface NoDataSourcesStateProps {
//   onAddDataSource: () => void;
// }
// // --- MODIFIED: This component is updated ---
// const NoDataSourcesState: React.FC<NoDataSourcesStateProps> = ({ onAddDataSource }) => {
//   return (
    
//     <Box
//       sx={{
//         // It now fills the height of its parent container
//         height: '100%', 
//         display: 'flex', 
//         flexDirection: 'column',
//         justifyContent: 'center', 
//         alignItems: 'center', 
//         textAlign: 'center',
//         backgroundColor: '#FFFFFF', 
//         borderRadius: '16px',
//         // A subtle border to match the design style
//         // border: '1px solid #E0E0E0', 
//         padding:'175px'
//       }}
//     >
        
//       <img src="/icons/no-cost-analysis.svg" alt="Nothing to display" style={{ width: '150px', height: '150px', marginBottom: '24px' }} />
//       <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontFamily:'Inter' }}>
//         Nothing to display yet
//       </Typography>
//       <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: '450px', fontFamily:'Inter', lineHeight: 1.6 }}>
//         To view LLM analytics, add a data source using the button below. You can also add it from the Data Source page.
//       </Typography>
//       <Button variant="contained" onClick={onAddDataSource}
// sx={{ backgroundColor:'#0064FF', width:'179px',height:'36px', textTransform: 'none', fontSize:'14px', fontWeight: 500, borderRadius: '8px',color:' #FFFFFF' }}      >
//         Add LLM Data Source
//       </Button>
//     </Box>
//   );
// };

// interface LangfuseSelectionModalProps {
//   open: boolean;
//   onClose: () => void;
//   tenantId: string;
//   onSuccess: (selection: 'MANAGED' | 'SELF_HOSTED', payload?: any) => void;
// }
// const LangfuseSelectionModal: React.FC<LangfuseSelectionModalProps> = ({ open, onClose, tenantId, onSuccess }) => {
//     const [selection, setSelection] = useState<'MANAGED' | 'SELF_HOSTED' | ''>('');
//     const [configure, { loading, error }] = useMutation(CONFIGURE_LLM_OBS);
//     const navigate = useNavigate();

//     const [formState, setFormState] = useState({
//         langfuseUrl: '',
//         langfuseEmail: '',
//         langfusePassword: '',
//         clickhouseUrl: '',
//         clickhouseUsername: '',
//         clickhousePassword: ''
//     });

//     const [formErrors, setFormErrors] = useState({
//         langfuseUrl: '',
//         langfuseEmail: '',
//         clickhouseUrl: ''
//     });

//     const validateField = (name: string, value: string): string => {
//         switch (name) {
//             case 'langfuseEmail':
//                 if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
//                     return "Please enter a valid email address.";
//                 }
//                 break;
//             case 'langfuseUrl':
//             case 'clickhouseUrl':
//                 if (!value.startsWith('http://') && !value.startsWith('https://')) {
//                     return "URL must start with http:// or https://";
//                 }
//                 if (/[^a-zA-Z0-9:/.-]/.test(value)) {
//                     return "URL contains invalid characters.";
//                 }
//                 break;
//             default:
//                 break;
//         }
//         return '';
//     };

//     const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
//         const { name, value } = event.target;
//         setFormState(prevState => ({ ...prevState, [name]: value }));
//         const errorMessage = validateField(name, value);
//         setFormErrors(prevErrors => ({ ...prevErrors, [name]: errorMessage }));
//     };

//     const isSelfHostedFormFilled = 
//         !!formState.langfuseUrl &&
//         !!formState.langfuseEmail &&
//         !!formState.langfusePassword &&
//         !!formState.clickhouseUrl &&
//         !!formState.clickhouseUsername &&
//         !!formState.clickhousePassword;
    
//     const hasValidationErrors = Object.values(formErrors).some(msg => msg !== '');

//     const getIsButtonDisabled = () => {
//         if (loading) {
//             return true;
//         }
//         if (selection === 'MANAGED') {
//             return false;
//         }
//         if (selection === 'SELF_HOSTED') {
//             return !isSelfHostedFormFilled || hasValidationErrors;
//         }
//         return true;
//     };


//     const externalLabelStyle = { fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 400, color: '#333333', marginBottom: '6px', display: 'block' };
//     const sxTextField = {
//       '& .MuiOutlinedInput-root': {
//         '& fieldset': { borderRadius: '8px' },
//         '&.Mui-focused fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
//       },
//       '& .MuiInputBase-input': {
//         fontFamily: 'Inter, sans-serif', color: '#333333', fontSize: '12px',
//         '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
//           WebkitBoxShadow: '0 0 0 30px white inset !important', WebkitTextFillColor: `#333333 !important`,
//           transition: 'background-color 5000s ease-in-out 0s',
//         },
//       },
//       '& .MoiInputBase-input::placeholder': {
//         fontFamily: 'Inter, sans-serif', color: 'rgba(185, 185, 185, 1)', fontSize: '12px', opacity: 1,
//       },
//     };

//     const handleSubmit = async () => {
//         if (getIsButtonDisabled()) return;
        
//         const input = { tenantId, configurationType: selection, ...formState };
        
//         try {
//             await configure({ variables: { input } });
//             const payload = selection === 'SELF_HOSTED' ? formState : undefined;
//             onSuccess(selection as 'MANAGED' | 'SELF_HOSTED', payload);
//             navigate('/data-sources/add', { state: { defaultProvider: 'llm', selfHostedPayload: payload } });
//         } catch (e) {
//             console.error("Failed to save configuration:", e);
//         }
//     };
    
//     const RadioOption = ({ value, title, description }: { value: 'MANAGED' | 'SELF_HOSTED', title: string, description: string }) => {
//         const isSelected = selection === value;
//         return (
//             <Box
//                 onClick={() => setSelection(value)}
//                 sx={{
//                     width: '100%',
//                     minHeight: 85,
//                     padding: '10px',
//                     borderStyle: 'solid',
//                     borderWidth: '1px',
//                     borderRadius: '8px',
//                     display: 'flex',
//                     alignItems: 'flex-start',
//                     cursor: 'pointer',
//                     boxSizing: 'border-box',
//                     borderColor: isSelected ? '#0064FF' : '#ECECEC',
//                     backgroundColor: isSelected ? '#F3F8FE' : '#FFFFFF',
//                 }}
//             >
//                 <Radio 
//                     checked={isSelected} 
//                     value={value}
//                     size="small"
//                     sx={{ 
//                         p: 0, 
//                         mr: '6px', 
//                         mt: '1px',
//                         '&.Moi-checked': {
//                           color: '#0064FF',
//                         },
//                     }} 
//                 />
//                 <Box>
//                     <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '14px', lineHeight: '21px', color: '#666666' }}>
//                         {title}
//                     </Typography>
//                     <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '12px', lineHeight: '18px', color: '#666666', mt: 0.5 }}>
//                         {description}
//                     </Typography>
//                 </Box>
//             </Box>
//         );
//     };

//     return (
//         <Dialog 
//             open={open} 
//             onClose={onClose} 
//             PaperProps={{ 
//                 sx: { 
//                     width: 600, 
//                     maxWidth: '95%',
//                     borderRadius: '16px',
//                     p: '8px',
//                     fontFamily: 'Inter',
//                     opacity: 1,
//                     position: 'relative',
//                     overflow: 'hidden',
//                     backgroundImage: "url('/textures/Texture.svg'), url('/images/Gradient_Popup.svg')",
//                     backgroundRepeat: ' no-repeat',
//                     backgroundPosition: 'top left, top right',
//                 } 
//             }}
//         >
//             <DialogContent sx={{ p: '16px', display: 'flex', flexDirection: 'column' }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
//                   <Box sx={{ 
//                       mr: 1.5,
//                       width: 56, 
//                       height: 56,
//                       bgcolor: '#E5EFFF', 
//                       borderRadius: '10px', 
//                       display: 'flex', 
//                       alignItems: 'center', 
//                       justifyContent: 'center',
//                   }}>
//                       <img src="/icons/langfuse_selection.svg" alt="Langfuse Icon" style={{ width: '26px', height: '19px' }}/>
//                   </Box>
//                     <Box>
//                         <Typography sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '20px', lineHeight: '115%', color: '#333333' }}>
//                             Langfuse Selection
//                         </Typography>
//                         <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '14px', lineHeight: '150%', color: '#666666' }}>
//                             To proceed, select the Langfuse option for this tenant.
//                         </Typography>
//                     </Box>
//                 </Box>

//                 <Box sx={{ p: '16px', borderRadius: '12px', my: 3, display: 'flex', alignItems: 'flex-start', gap: '12px', bgcolor: '#F6F4E5', border: '1px solid #A59500' }}>
//                     <img src="/icons/watch_icon.svg" alt="Note Icon" style={{ width: '18px', height: '21px' }}/>
//                     <Box>
//                       <Typography sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '14px', lineHeight: '20px', color: '#333333' }}>
//                           Please note
//                       </Typography>
//                       <Typography component="ul" sx={{ pl: 2.5, m: 0, mt: 1, fontFamily: 'Inter', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#666666', '& li': { mb: 0.5 } }}>
//                           <li>This is a one time selection, once confirmed it cannot be changed.</li>
//                           <li>All the future LLM Data Source creation for this tenant will use the selected option.</li>
//                       </Typography>
//                     </Box>
//                 </Box>
                
//                 <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
//                     <RadioOption value="MANAGED" title="Use our langfuse solution" description="Lorem ipsum dolor sum it. Lorem ipsum dolor sum it." />
//                     <RadioOption value="SELF_HOSTED" title="Use self hosted langfuse solution" description="Lorem ipsum dolor sum it. Lorem ipsum dolor sum it."/>
//                 </Box>
//                 {selection === 'SELF_HOSTED' && (
//                     <Box sx={{ mt: 3 }}>
//                         <Typography sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '16px', mb: 2 }}>
//                             Please provide us with your self hosted Langfuse details
//                         </Typography>
//                         {/* Row 1: Langfuse URL | Clickhouse URL */}
//                         <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1.5 }}>
//                             <Box sx={{ flex: 1, flexDirection: 'column' }}>
//                                 <InputLabel htmlFor="langfuseUrl" sx={externalLabelStyle}>Langfuse URL *</InputLabel>
//                                 <TextField required fullWidth id="langfuseUrl" name="langfuseUrl" size="small" placeholder="Enter Langfuse URL" sx={sxTextField} value={formState.langfuseUrl} onChange={handleInputChange} 
//                                     error={!!formErrors.langfuseUrl}
//                                     helperText={formErrors.langfuseUrl}
//                                     InputProps={{ endAdornment: formState.langfuseUrl && !formErrors.langfuseUrl && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} />
//                             </Box>
//                             <Box sx={{ flex: 1, flexDirection: 'column' }}>
//                                 <InputLabel htmlFor="clickhouseUrl" sx={externalLabelStyle}>Clickhouse URL *</InputLabel>
//                                 <TextField required fullWidth id="clickhouseUrl" name="clickhouseUrl" size="small" placeholder="Enter Clickhouse URL" sx={sxTextField} value={formState.clickhouseUrl} onChange={handleInputChange} 
//                                     error={!!formErrors.clickhouseUrl}
//                                     helperText={formErrors.clickhouseUrl}
//                                     InputProps={{ endAdornment: formState.clickhouseUrl && !formErrors.clickhouseUrl && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} />
//                             </Box>
//                         </Box>
//                         {/* Row 2: Langfuse Email | Clickhouse DB Username */}
//                         <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1.5 }}>
//                             <Box sx={{ flex: 1, flexDirection: 'column' }}>
//                                 <InputLabel htmlFor="langfuseEmail" sx={externalLabelStyle}>Langfuse Email *</InputLabel>
//                                 <TextField required fullWidth id="langfuseEmail" name="langfuseEmail" size="small" placeholder="Enter Langfuse Email" sx={sxTextField} value={formState.langfuseEmail} onChange={handleInputChange} 
//                                     error={!!formErrors.langfuseEmail}
//                                     helperText={formErrors.langfuseEmail}
//                                     InputProps={{ endAdornment: formState.langfuseEmail && !formErrors.langfuseEmail && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} />
//                             </Box>
//                             <Box sx={{ flex: 1, flexDirection: 'column' }}>
//                                 <InputLabel htmlFor="clickhouseUsername" sx={externalLabelStyle}>Clickhouse DB Username *</InputLabel>
//                                 <TextField required fullWidth id="clickhouseUsername" name="clickhouseUsername" size="small" placeholder="Enter Clickhouse DB Username" sx={sxTextField} value={formState.clickhouseUsername} onChange={handleInputChange} InputProps={{ endAdornment: formState.clickhouseUsername && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} />
//                             </Box>
//                         </Box>
//                         {/* Row 3: Langfuse Password | Clickhouse DB Password */}
//                         <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1.5 }}>
//                             <Box sx={{ flex: 1, flexDirection: 'column' }}>
//                                 <InputLabel htmlFor="langfusePassword" sx={externalLabelStyle}>Langfuse Password *</InputLabel>
//                                 <TextField required fullWidth id="langfusePassword" name="langfusePassword" type="password" size="small" placeholder="Enter Langfuse Password" sx={sxTextField} value={formState.langfusePassword} onChange={handleInputChange} InputProps={{ endAdornment: formState.langfusePassword && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} />
//                             </Box>
//                             <Box sx={{ flex: 1, flexDirection: 'column' }}>
//                                 <InputLabel htmlFor="clickhousePassword" sx={externalLabelStyle}>Clickhouse DB Password *</InputLabel>
//                                 <TextField required fullWidth id="clickhousePassword" name="clickhousePassword" type="password" size="small" placeholder="Enter Clickhouse DB Password" sx={sxTextField} value={formState.clickhousePassword} onChange={handleInputChange} InputProps={{ endAdornment: formState.clickhousePassword && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} />
//                             </Box>
//                         </Box>
//                     </Box>
//                 )}
//                 {/* {selection === 'SELF_HOSTED' && (
//                     <Box sx={{ mt: 3 }}>
//                         <Typography sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '16px', mb: 2 }}>
//                             Please provide us with your self hosted Langfuse details
//                         </Typography>
//                         <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1.5 }}>
//                             <Box sx={{ flex: 1, flexDirection: 'column' }}>
//                                 <InputLabel htmlFor="langfuseUrl" sx={externalLabelStyle}>Langfuse URL *</InputLabel>
//                                 <TextField required fullWidth id="langfuseUrl" name="langfuseUrl" size="small" placeholder="Enter Langfuse URL" sx={sxTextField} value={formState.langfuseUrl} onChange={handleInputChange} 
//                                     error={!!formErrors.langfuseUrl}
//                                     helperText={formErrors.langfuseUrl}
//                                     InputProps={{ endAdornment: formState.langfuseUrl && !formErrors.langfuseUrl && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} />
//                             </Box>
//                             <Box sx={{ flex: 1, flexDirection: 'column' }}>
//                                 <InputLabel htmlFor="langfuseEmail" sx={externalLabelStyle}>Langfuse Email *</InputLabel>
//                                 <TextField required fullWidth id="langfuseEmail" name="langfuseEmail" size="small" placeholder="Enter Langfuse Email" sx={sxTextField} value={formState.langfuseEmail} onChange={handleInputChange} 
//                                     error={!!formErrors.langfuseEmail}
//                                     helperText={formErrors.langfuseEmail}
//                                     InputProps={{ endAdornment: formState.langfuseEmail && !formErrors.langfuseEmail && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} />
//                             </Box>
//                         </Box>
//                         <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1.5 }}>
//                             <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="langfusePassword" sx={externalLabelStyle}>Langfuse Password *</InputLabel><TextField required fullWidth id="langfusePassword" name="langfusePassword" type="password" size="small" placeholder="Enter Langfuse Password" sx={sxTextField} value={formState.langfusePassword} onChange={handleInputChange} InputProps={{ endAdornment: formState.langfusePassword && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} /></Box>
//                             <Box sx={{ flex: 1, flexDirection: 'column' }}>
//                                 <InputLabel htmlFor="clickhouseUrl" sx={externalLabelStyle}>Clickhouse URL *</InputLabel>
//                                 <TextField required fullWidth id="clickhouseUrl" name="clickhouseUrl" size="small" placeholder="Enter Clickhouse URL" sx={sxTextField} value={formState.clickhouseUrl} onChange={handleInputChange} 
//                                     error={!!formErrors.clickhouseUrl}
//                                     helperText={formErrors.clickhouseUrl}
//                                     InputProps={{ endAdornment: formState.clickhouseUrl && !formErrors.clickhouseUrl && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} />
//                             </Box>
//                         </Box>
//                         <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1.5 }}>
//                             <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="clickhouseDbUsername" sx={externalLabelStyle}>Clickhouse DB Username *</InputLabel><TextField required fullWidth id="clickhouseDbUsername" name="clickhouseDbUsername" size="small" placeholder="Enter Clickhouse DB Username" sx={sxTextField} value={formState.clickhouseDbUsername} onChange={handleInputChange} InputProps={{ endAdornment: formState.clickhouseDbUsername && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} /></Box>
//                             <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="clickhouseDbPassword" sx={externalLabelStyle}>Clickhouse DB Password *</InputLabel><TextField required fullWidth id="clickhouseDbPassword" name="clickhouseDbPassword" type="password" size="small" placeholder="Enter Clickhouse DB Password" sx={sxTextField} value={formState.clickhouseDbPassword} onChange={handleInputChange} InputProps={{ endAdornment: formState.clickhouseDbPassword && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} /></Box>
//                         </Box>
//                     </Box>
//                 )} */}

//                 {error && <Alert severity="error" sx={{ mt: 2 }}>{error.message}</Alert>}
//             </DialogContent>

//             <DialogActions sx={{ p: '24px', pt: 1, mt: 'auto' }}>
//                 <Button 
//                     onClick={onClose} 
//                     sx={{ 
//                         width: '79px',
//                         height: '36px',
//                         backgroundColor: '#F2F7FF',
//                         color: '#0064FF',
//                         borderRadius: '8px',
//                         py: '10px',
//                         px: '16px',
//                         border: 'none',
//                         textTransform: 'none',
//                         fontFamily: 'Inter',
//                         fontWeight: 500,
//                         fontSize: '14px',
//                         lineHeight: '16px',
//                         '&:hover': {
//                             backgroundColor: '#E5F1FF'
//                         }
//                     }}
//                 >
//                     Cancel
//                 </Button>
//                 <Button 
//                     variant="contained" 
//                     onClick={handleSubmit} 
//                     disabled={getIsButtonDisabled()}
//                     sx={{
//                         boxShadow: 'none',
//                         width: '180px',
//                         height: '36px',
//                         borderRadius: '8px',
//                         py: '10px',
//                         px: '16px',
//                         gap: '4px',
//                         textTransform: 'none', 
//                         backgroundColor: '#0064FF',
//                         color: '#FFFFFF',
//                         fontFamily: 'Inter',
//                         fontWeight: 500,
//                         fontSize: '14px',
//                         lineHeight: '16px',
//                         '&:hover': {
//                             backgroundColor: '#0053D6',
//                             boxShadow: 'none',
//                         },
//                         '&.Moi-disabled': {
//                             backgroundColor: '#E5E7EB',
//                             color: '#9CA3AF'
//                         }
//                     }}
//                 >
//                     {loading ? <CircularProgress size={24} color="inherit" /> : 'Confirm and Proceed'}
//                 </Button>
//             </DialogActions>
//         </Dialog>
//     );
// };


// // --- Main Page Component ---

// interface LLMObservabilityProps {
//   tenantId?: string;
// }

// const LLMObservability: React.FC<LLMObservabilityProps> = ({ tenantId }) => {
//     const [isSelectionModalOpen, setSelectionModalOpen] = useState(false);
//     const navigate = useNavigate();

//     const { data: configData, loading: configLoading, error: configError, refetch: refetchConfig } = useQuery(GET_LLM_CONFIG, {
//         variables: { tenantId },
//         skip: !tenantId,
//         fetchPolicy: 'network-only',
//     });
    
//     const isConfigured = !!configData?.getLlmObservabilityConfig;

//     const { data: dataSourcesData, loading: dataSourcesLoading, error: dataSourcesError } = useQuery(GET_LLM_DATA_SOURCES, {
//         variables: { tenantId },
//         skip: !tenantId,
//         fetchPolicy: 'network-only', 
//     });

//     const handleAddDataSourceClick = () => {
//         if (isConfigured) {
//             navigate('/data-sources/add', { state: { defaultProvider: 'llm' } });
//         } else {
//             setSelectionModalOpen(true);
//         }
//     };

//     const handleConfigurationSuccess = (selection: 'MANAGED' | 'SELF_HOSTED', payload?: any) => {
//         setSelectionModalOpen(false);
//         refetchConfig();
        
//         if (selection === 'SELF_HOSTED') {
//             console.log("Configuration successful for SELF_HOSTED with payload:", payload);
//         } else {
//             console.log("Configuration successful for MANAGED!");
//         }
//     };

//     if (!tenantId) {
//         return <NoTenantEmptyState />;
//     }

//     if (configLoading || dataSourcesLoading) {
//         return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>;
//     }

//     if (configError || dataSourcesError) {
//         console.error("Error fetching data:", configError || dataSourcesError);
//         return <DataFetchErrorState />;
//     }

//     const hasDataSources = dataSourcesData?.llmDataSourcesByTenant?.length > 0;

//     if (hasDataSources) {
//         return <LLMpage tenantId={tenantId} />;
//     }

//     // --- MODIFIED: The parent Box now controls padding and height ---
//     return (
//         <Box sx={{ p: 3, background: '#F9F9F9', minHeight: 'calc(100vh - 100px)', boxSizing: 'border-box' }}>
//             <NoDataSourcesState onAddDataSource={handleAddDataSourceClick} />
            
//             {tenantId && (
//                 <LangfuseSelectionModal
//                     open={isSelectionModalOpen}
//                     onClose={() => setSelectionModalOpen(false)}
//                     tenantId={tenantId}
//                     onSuccess={handleConfigurationSuccess}
//                 />
//             )}
//         </Box>
//     );
// };

// export default LLMObservability;